import { cannotText } from "./line-type";
import { classifyTransfer } from "./front-desk-transfer";
import type { TransferTurn } from "./front-desk-transfer";

/** The front desk: what happened on a call, and whether it still needs somebody. Badges are
 * derived, not stored, since the list, detail page and tab strip read one row and three copies
 * would disagree. */

export * from "./front-desk-transfer";

export type CallFlag =
  | "connected"
  | "declined"
  | "voicemail"
  | "wrong_number"
  | "ai_answered"
  | "no_answer"
  | "message"
  | "handled"
  | "robocall"
  | "screened"
  | "escalated"
  | "resident"
  | "unverified"
  | "no_speech"
  | "recorded"
  /** The caller's number cannot take a text, so texting them is a dead end. */
  | "cant_text";

export interface CallEvent {
  event: string;
  detail?: string | null;
}

export interface FlaggableCall {
  /** "inbound" unless the call left from the desk. Absent on rows from before
   *  the column existed, which were all inbound. */
  direction?: string | null;
  /** What the line-type cache says about the caller's number, when looked up. Absent means nobody
   * consulted it, which is not "nothing": no badge rather than a wrong one. */
  lineStatus?: string | null;
  kind: string;
  triage: string;
  transfer: { status?: string; name?: string | null; section?: string | null } | null;
  transferTranscript: readonly TransferTurn[] | null;
  transcript: readonly unknown[] | null;
  summary: { automated?: boolean } | null;
  events: readonly CallEvent[] | null;
  recordingUrl: string | null;
  transferRecordingUrl: string | null;
}

export interface CallFlagResult {
  flags: CallFlag[];
  /** Who the call was transferred to, for grouping by staff member. */
  staffName: string | null;
  staffSection: string | null;
  /** Playable audio exists, for the whole call or just the transferred leg. */
  hasRecording: boolean;
}

const hasEvent = (call: FlaggableCall, event: string) =>
  (call.events ?? []).some((entry) => entry.event === event);

/** Everything the badges and the tab strip are built from. */
export function callFlags(call: FlaggableCall): CallFlagResult {
  const flags: CallFlag[] = [];
  const transfer = call.transfer;

  if (transfer) {
    switch (classifyTransfer(transfer.status, call.transferTranscript)) {
      case "answered":
        flags.push("connected");
        break;
      case "declined":
        flags.push("declined");
        break;
      case "voicemail":
        flags.push("voicemail");
        break;
      case "wrong_number":
        flags.push("wrong_number");
        break;
      case "ai_answered":
        flags.push("ai_answered");
        break;
      case "missed":
        flags.push("no_answer");
        break;
      default:
        // Connected but nothing transcribed is still a reached transfer.
        if (transfer.status === "connected") flags.push("connected");
        break;
    }
  }

  if (call.kind === "message") flags.push("message");
  /* Handled is a triage STATE, not a property of messages: a failed transfer someone rang back
   * about is handled too. Restricting it to `kind === "message"` hid 105 of 161 staff-handled calls
   * on Pine Lodge. */
  if (call.triage === "done") flags.push("handled");
  if (call.summary?.automated) flags.push("robocall");

  // A screened caller is recorded on the timeline. The caller never learns it,
  // but the team should be able to find them.
  if (hasEvent(call, "call blocked") || hasEvent(call, "classified as screened-caller message")) {
    flags.push("screened");
  }
  // The reverse case: put through to a named person ahead of the usual routing.
  if (hasEvent(call, "call escalated")) flags.push("escalated");
  if (hasEvent(call, "resident verify") || hasEvent(call, "resident note")) flags.push("resident");

  // A resident check that was tried and failed. Not every transfer: staff took that for
  // "this caller is suspect" on nearly every call (Ashok, 5 Oct).
  const verified = (call.events ?? []).some(
    (entry) => entry.event === "resident verify" && entry.detail === "matched",
  );
  if (!verified && flags.includes("resident")) flags.push("unverified");

  /* Nobody transcribes a call placed from the desk — Sarah is not on it — so
     an empty transcript there is not "nobody spoke", and the chip that says so
     was on every outbound row. */
  if (call.direction !== "outbound" && (call.transcript?.length ?? 0) === 0) {
    flags.push("no_speech");
  }
  if (cannotText(call.lineStatus)) flags.push("cant_text");

  const hasRecording = Boolean(
    (call.recordingUrl ?? "").trim() || (call.transferRecordingUrl ?? "").trim(),
  );
  if (hasRecording) flags.push("recorded");

  return {
    flags,
    staffName: transfer?.name ?? null,
    staffSection: transfer?.section ?? null,
    hasRecording,
  };
}

export const CALL_TABS = [
  "all",
  "attention",
  "handled",
  "messages",
  "transfers",
  "robocalls",
] as const;

export type CallTab = (typeof CALL_TABS)[number];

/** Which telephone a call touched: the building's front desk line, or a person's own number. */
export const CALL_LINES = ["desk", "own"] as const;

export type CallLine = (typeof CALL_LINES)[number];

/** Does a call belong in this tab? Takes the raw triage state too, so a message already marked
 * handled stops needing attention. */
export function matchesCallTab(tab: CallTab, result: CallFlagResult, triage: string): boolean {
  switch (tab) {
    case "attention":
      // The calls worth somebody's time now: a transfer that failed to reach
      // anyone or was turned down, or a caller told somebody would ring back.
      return (
        triage !== "done" &&
        (result.flags.includes("voicemail") ||
          result.flags.includes("wrong_number") ||
          result.flags.includes("ai_answered") ||
          result.flags.includes("no_answer") ||
          result.flags.includes("declined") ||
          (result.flags.includes("message") && !result.flags.includes("robocall")))
      );
    case "handled":
      return result.flags.includes("handled");
    case "messages":
      return result.flags.includes("message") && !result.flags.includes("robocall");
    case "transfers":
      return result.staffName !== null;
    case "robocalls":
      return result.flags.includes("robocall");
    default:
      return true;
  }
}

/** Whether this call can be triaged: only the message pathway has triage; a standard call cannot be
 * "handled". */
export function isTriageable(call: { triage: string }): boolean {
  return call.triage !== "none";
}

export interface AssignmentWindow {
  days: readonly string[];
  startTime: string;
  endTime: string;
  timeOff: readonly string[];
  isActive: boolean;
  isFallback: boolean;
}

/** Whether this staff assignment is on duty at a local time. A window ending before it starts
 * crosses midnight (a night shift); treating it as empty stops overnight cover receiving
 * anything. */
export function assignmentOnDuty(
  assignment: AssignmentWindow,
  when: { day: string; time: string; date: string },
): boolean {
  if (!assignment.isActive) return false;
  if (assignment.timeOff.includes(when.date)) return false;
  if (assignment.days.length > 0 && !assignment.days.includes(when.day)) return false;

  const { startTime, endTime } = assignment;
  if (startTime === endTime) return true;
  if (startTime < endTime) return when.time >= startTime && when.time < endTime;
  return when.time >= startTime || when.time < endTime;
}

/** Who a call for this section should reach: people on duty first in configured order, a fallback
 * only when nobody is (a fallback answering while the assignee was available means routing
 * failed). */
export function routeTo<T extends AssignmentWindow & { sortOrder: number }>(
  assignments: readonly T[],
  when: { day: string; time: string; date: string },
): T[] {
  const onDuty = assignments
    .filter((assignment) => !assignment.isFallback && assignmentOnDuty(assignment, when))
    // oxlint-disable-next-line unicorn/no-array-sort -- a fresh array; the phone app's Hermes has no toSorted
    .sort((left, right) => left.sortOrder - right.sortOrder);
  if (onDuty.length > 0) return onDuty;

  return (
    assignments
      .filter((assignment) => assignment.isFallback && assignment.isActive)
      // oxlint-disable-next-line unicorn/no-array-sort -- a fresh array; the phone app's Hermes has no toSorted
      .sort((left, right) => left.sortOrder - right.sortOrder)
  );
}

/** Whether the building is inside its after-hours window. */
export function isAfterHours(
  settings: { afterHoursEnabled: boolean; afterHoursStart: string; afterHoursEnd: string },
  time: string,
): boolean {
  if (!settings.afterHoursEnabled) return false;
  const { afterHoursStart: start, afterHoursEnd: end } = settings;
  if (start === end) return false;
  // After hours normally crosses midnight, which is the whole point of it.
  return start < end ? time >= start && time < end : time >= start || time < end;
}
